import { useAppSelector } from '@/redux/hooks'
import {
  ChevronIcon,
  Menu,
  MenuButton,
  MenuItems
} from '@itsrever/design-system'
import { clsx } from 'clsx'
import { useTranslation } from 'react-i18next'
import { KeyboardEvent, useMemo } from 'react'
import {
  findGroupByKey,
  findStoreGroup,
  toGroupName
} from '@/domain/store-group'
import { EcommerceList } from './ecommerce-list'
import { useStoreGroups } from '@/hooks/use-store-groups'

export const EcommerceSelector = () => {
  const { t } = useTranslation()

  const settings = useAppSelector((s) => s.settingsApi.settings.response)
  const generalData = useAppSelector((store) => store.generalData)
  const user = useAppSelector((s) => s.userApi.getMe.response.user)
  const ecommerceList = useMemo(() => user?.e_commerces_ids || [], [user])

  const groups = useStoreGroups()
  const selectedEcommerce = generalData.selectedEcommerce
  const selectedGroup = findGroupByKey(groups, generalData.selectedGroup)
  const groupName =
    (selectedEcommerce && findStoreGroup(groups, selectedEcommerce)?.name) ||
    selectedGroup?.name ||
    toGroupName(user?.group, t('selector_component.all'))
  const faviconURL = settings.theme?.favicon_url || ''
  const disabled = generalData.disableSelector

  return (
    <>
      {ecommerceList?.length ? (
        <div className="p-4 pb-0">
          <Menu>
            <MenuButton
              className={clsx(
                'flex w-full items-center justify-between rounded-lg',
                selectedEcommerce ? 'p-2' : 'px-3 py-1.5',
                'bg-[#2C3242] text-white outline outline-1 outline-[#353C4E]',
                disabled && 'opacity-50'
              )}
              styled={false}
              disabled={disabled}
            >
              {/* Groups have no logo, and in the group view settings are the first store's. */}
              {selectedEcommerce ? (
                <div className="flex w-full items-center gap-2 overflow-hidden">
                  <div
                    className={clsx(
                      'size-8 rounded-lg bg-contain bg-center bg-no-repeat',
                      'flex items-center justify-center',
                      faviconURL ? 'bg-white' : 'bg-gray-400'
                    )}
                    style={{
                      backgroundImage: `url(${faviconURL})`
                    }}
                  >
                    <span className="font-semibold opacity-60">
                      {faviconURL
                        ? null
                        : selectedEcommerce.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="min-w-0 text-left">
                    <p className="truncate text-sm">{selectedEcommerce}</p>
                    <div className="flex">
                      <span className="rounded bg-blue-400/15 px-1.5 text-xs font-medium leading-4 text-blue-300">
                        {t('selector_component.mode_store')}
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                // Name first in the DOM so screen readers read it first; the badge still shows above it.
                <div className="flex min-w-0 flex-col-reverse gap-1 text-left">
                  <p className="truncate text-sm font-medium">{groupName}</p>
                  <p className="flex min-w-0 items-center gap-1.5 text-xs font-medium">
                    <span className="shrink-0 rounded bg-emerald-400/15 px-1.5 text-xs font-medium uppercase leading-4 tracking-wider text-emerald-300">
                      {t('selector_component.mode_group')}
                    </span>
                    <span className="truncate text-neutral-400">
                      {t('selector_component.group_subtitle', {
                        count:
                          selectedGroup?.ecommerces.length ??
                          ecommerceList.length
                      })}
                    </span>
                  </p>
                </div>
              )}
              <ChevronIcon
                className={clsx('text-[#99A0AE] transition-all duration-200')}
              />
            </MenuButton>

            <MenuItems
              transition
              anchor={{
                to: 'bottom',
                gap: '8px'
              }}
              className={clsx(
                'w-[var(--button-width)] rounded-lg p-2',
                'border border-solid border-[#353C4E] bg-[#1D222E]',
                'transition duration-150 ease-out data-[closed]:scale-95 data-[closed]:opacity-0'
              )}
              styled={false}
              onKeyDownCapture={keepMenuOpenOnEnter}
            >
              <EcommerceList />
            </MenuItems>
          </Menu>
          <hr className="mt-4 border-[#282E3B]" />
        </div>
      ) : null}
    </>
  )
}

// Headless UI closes the menu on Enter, so items that expand in place handle it first.
const keepMenuOpenOnEnter = (e: KeyboardEvent<HTMLElement>) => {
  if (e.key !== 'Enter') return
  const active = e.currentTarget.querySelector<HTMLElement>(
    '[data-focus][data-keeps-menu-open]'
  )
  if (!active) return
  e.preventDefault()
  e.stopPropagation()
  active.click()
}
